/**
 * Browser-side image compressor.
 *
 * Motivation: Vercel serverless functions cap request bodies at 4.5 MB on
 * all plans. Phone photos routinely weigh 6–12 MB — without compression,
 * the admin would hit a 413 from production and not understand why. We
 * re-encode images client-side to JPEG at a quality ladder so the upload
 * comfortably fits under that ceiling.
 *
 * Non-image inputs are returned verbatim (PDFs go through untouched).
 * Already-small images are returned verbatim too — we avoid a lossy round
 * trip when the file's already fine.
 */

export type CompressOptions = {
  /** Hard target, in bytes. Compression keeps lowering quality until below this. */
  maxBytes: number;
  /**
   * Longest edge (pixels) the image is resized to. Defaults to 2000 —
   * product photos don't need more than that for web display. Aspect ratio
   * is preserved.
   */
  maxEdge?: number;
};

/**
 * Try to compress `file` to at most `maxBytes`. Returns `null` if the
 * smallest encoded version is still over budget — caller should surface
 * an error.
 *
 * Non-images (PDFs, etc.) bypass compression:
 *   - under `maxBytes` → returned unchanged
 *   - over `maxBytes`  → `null` (nothing we can do)
 */
export async function compressImage(
  file: File,
  opts: CompressOptions,
): Promise<File | null> {
  const { maxBytes, maxEdge = 2000 } = opts;

  // Non-image: no compression available. Accept if already small.
  if (!file.type.startsWith('image/')) {
    return file.size <= maxBytes ? file : null;
  }

  // Already small enough → don't re-encode (avoids quality loss).
  if (file.size <= maxBytes) return file;

  const bitmap = await loadImage(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Canvas context unavailable (very rare — headless, locked-down
    // browser). Bail and let the caller fail loudly.
    return null;
  }
  ctx.drawImage(bitmap, 0, 0, width, height);

  // Quality ladder — stop at the first result that fits. 0.55 is the floor;
  // below that, JPEG artifacts get objectionable on product photography.
  const qualities = [0.85, 0.75, 0.65, 0.55];
  for (const q of qualities) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', q);
    if (blob && blob.size <= maxBytes) {
      return new File([blob], renameToJpeg(file.name), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * `createImageBitmap` respects EXIF orientation on most browsers and is
 * faster than `<img>` + onload. We fall back to the img path on older
 * Safari (<16.4) where the EXIF flag isn't respected.
 */
async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall through to HTMLImageElement path.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image_decode_failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function fitWithin(
  w: number,
  h: number,
  maxEdge: number,
): { width: number; height: number } {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const scale = maxEdge / Math.max(w, h);
  return {
    width: Math.round(w * scale),
    height: Math.round(h * scale),
  };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

function renameToJpeg(name: string): string {
  return name.replace(/\.[^.]+$/, '') + '.jpg';
}
